import { useEffect, useState } from "react";
import { AnalyticsEvent } from "../../types/Analytics";
import { firestore } from "../firebase";

type Options = {
  communityId?: string;
  startDate?: Date;
  endDate?: Date;
  eventType?: string;
  limit?: number;
};

export const useAnalyticsEvents = ({
  communityId,
  startDate,
  endDate,
  eventType,
  limit = 1000,
}: Options = {}) => {
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!communityId) {
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let query = firestore
      .collection("Analytics")
      .where("community_id", "==", communityId)
      .orderBy("ts", "desc")
      .limit(limit);

    // Apply date filters if provided
    if (startDate) {
      query = query.where("ts", ">=", startDate.getTime());
    }
    if (endDate) {
      query = query.where("ts", "<=", endDate.getTime());
    }

    // Apply event type filter if provided
    if (eventType && eventType !== "all") {
      query = query.where("event", "==", eventType);
    }

    const unsubscribe = query.onSnapshot(
      (snapshot) => {
        const fetchedEvents: AnalyticsEvent[] = [];
        snapshot.forEach((doc) => {
          fetchedEvents.push({
            id: doc.id,
            ...doc.data(),
          } as AnalyticsEvent);
        });
        setEvents(fetchedEvents);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching analytics events", err);
        setError(err.message);
        setEvents([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [communityId, startDate, endDate, eventType, limit]);

  return { events, loading, error };
};

// Function to get analytics for a specific date range without real-time updates
export const getAnalyticsEventsOnce = async ({
  communityId,
  startDate,
  endDate,
  eventType,
  limit = 1000,
}: Options): Promise<AnalyticsEvent[]> => {
  if (!communityId) {
    return [];
  }

  try {
    let query = firestore
      .collection("Analytics")
      .where("community_id", "==", communityId)
      .orderBy("ts", "desc")
      .limit(limit);

    if (startDate) {
      query = query.where("ts", ">=", startDate.getTime()) as any;
    }
    if (endDate) {
      query = query.where("ts", "<=", endDate.getTime()) as any;
    }
    if (eventType && eventType !== "all") {
      query = query.where("event", "==", eventType) as any;
    }

    const snapshot = await query.get();
    const events: AnalyticsEvent[] = [];

    snapshot.forEach((doc) => {
      events.push({
        id: doc.id,
        ...doc.data(),
      } as AnalyticsEvent);
    });

    return events;
  } catch (error) {
    console.error("Error fetching analytics events", error);
    return [];
  }
};
